import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { FilterModule } from '@wilson/filter';
import { TimelineTimeframe } from '@wilson/interfaces';
import { TimelinePageFrameComponent } from '@wilson/page-frame';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import {
  AssignShift,
  AssignmentShiftCardComponent,
  AssignmentsGanttChartComponent,
  AssignmentsGanttChartService,
  DeferredAbsenceCellViewCardComponent,
  DeferredAbsenceTimelineViewCardComponent,
  DeferredAssignmentShiftCardCellViewComponent,
  DeferredAssignmentShiftCardComponent,
  DeferredClusterCardComponent,
  DeferredShiftIconsComponent,
  DeferredStayCardComponent,
  DeferredUnassignedShiftCardCellViewComponent,
  ReassignShift,
  UnassignShift,
  UserKpisDataSourceService,
  ValidationDataSourceService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { TimelineInteractionService } from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
} from '@wilson/shift-timeline/state';
import { SideNavSettingsState } from '@wilson/side-nav-settings/state';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import {
  Observable,
  combineLatest,
  debounceTime,
  firstValueFrom,
  fromEvent,
  map,
  startWith,
} from 'rxjs';
import { ShiftTimelineDraftHelperService } from '../../services/shift-timeline-draft-helper/shift-timeline-draft-helper.service';
import { ShiftTimelineKpiDataSourceServiceService } from '../../shift-timeline-kpi-data-source-service.service';
import { UserTimelinesDraftInfoCardComponent } from '../user-timelines-draft-info-card/user-timelines-draft-info-card.component';
import { DraftTimelineValidationDataSourceServiceService } from './draft-timeline-validation-data-source-service.service';
import { ShiftTimelineDraftHeaderComponent } from './shift-timeline-draft-header/shift-timeline-draft-header.component';
import { TimelineDraftHeaderKPISectionComponent } from './timeline-draft-header-kpi-section/timeline-draft-header-kpi-section.component';
@Component({
  selector: 'wilson-shift-timeline-draft',
  imports: [
    CommonModule,
    TranslateModule,
    ShiftTimelineDraftHeaderComponent,
    AssignmentsGanttChartComponent,
    TimelineDraftHeaderKPISectionComponent,
    FeatureFlagsModule,
    FilterModule,
    FontAwesomeModule,
    TimelinePageFrameComponent,
    NzTooltipModule,
    UserTimelinesDraftInfoCardComponent,
    DeferredAssignmentShiftCardComponent,
    DeferredAbsenceCellViewCardComponent,
    DeferredAssignmentShiftCardCellViewComponent,
    DeferredUnassignedShiftCardCellViewComponent,
    DeferredClusterCardComponent,
    DeferredStayCardComponent,
    DeferredShiftIconsComponent,
    DeferredAbsenceTimelineViewCardComponent,
  ],
  templateUrl: './shift-timeline-draft.component.html',
  styleUrls: ['./shift-timeline-draft.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: ValidationDataSourceService,
      useClass: DraftTimelineValidationDataSourceServiceService,
    },
    {
      provide: UserKpisDataSourceService,
      useClass: ShiftTimelineKpiDataSourceServiceService,
    },
    TimelineInteractionService,
  ],
})
export class ShiftTimelineDraftComponent implements OnInit, AfterViewInit {
  private readonly store = inject(Store);
  protected readonly shiftTimelineDraftHelperService = inject(
    ShiftTimelineDraftHelperService,
  );

  private pixelExceptTimelineWidthWhenSidenavClose = 98;
  private pixelExceptTimelineWidthWhenSidenavOpen = 290;

  highlightedShiftIds$ = this.shiftTimelineDraftHelperService.changedShiftIds$;

  timeframe$: Observable<TimelineTimeframe | null> =
    this.shiftTimelineDraftHelperService.dateRange$;
  publicHolidaysRegion$ = this.store.select(AuthState.publicHolidaysRegion);
  determinedShiftActivities$ = this.store.select(
    ShiftTimelineDataState.determinedShiftActivities,
  );
  isKPICardOpen!: boolean;
  frameWidth$!: Observable<number>;
  public faChevronRight = faChevronRight;
  pixelWidthPerDay$ = this.store.select(
    ShiftTimelineSettingsState.pixelWidthPerDay(),
  );
  widthBreakpointPx = AssignmentShiftCardComponent.WIDTH_BREAKPOINT_PX();
  private assignmentsGanttChartService!: AssignmentsGanttChartService;

  isCellView$ = this.shiftTimelineDraftHelperService.isCellView$;
  readonly faExclamationCircle = faExclamationCircle;

  ngOnInit(): void {
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId) {
      this.isCellView$ = this.store.select(
        ShiftTimelinePreferredSettingsState.isCellView(userId),
      );
      this.frameWidth$ = combineLatest([
        this.store.select(SideNavSettingsState.getIsSideNavCollapsed(userId)),
        this.store.select(ShiftTimelineSettingsState.resourcesColumnWidthPx),
        fromEvent(window, 'resize').pipe(
          startWith(window.innerWidth),
          debounceTime(100),
          map(() => window.innerWidth),
        ),
      ]).pipe(
        map(
          ([isSidenavCollapsed, resourcesColumnWidthPx, windowWidth]: [
            boolean,
            number,
            number,
          ]) => {
            const pixelExceptTimelineWidth = isSidenavCollapsed
              ? this.pixelExceptTimelineWidthWhenSidenavClose
              : this.pixelExceptTimelineWidthWhenSidenavOpen;

            return (
              windowWidth - pixelExceptTimelineWidth - resourcesColumnWidthPx
            );
          },
        ),
      );
    }
  }

  async ngAfterViewInit(): Promise<void> {
    await this.fetchAssignmentOptimizerKpis();
  }

  async assignShiftAndUpdateUsersKpis(assignShift: AssignShift): Promise<void> {
    if (assignShift.updated.id) {
      this.shiftTimelineDraftHelperService.updateShiftsToUpdateMap(
        assignShift.updated.id,
        assignShift.userId,
      );

      this.shiftTimelineDraftHelperService.updateTrackedAssignmentsMap(
        assignShift.original.id,
        assignShift.userId,
        undefined,
      );

      await this.fetchAssignmentOptimizerKpis();
    }
  }

  async reassignShiftAndUpdateUsersKpis(
    reassignShift: ReassignShift,
  ): Promise<void> {
    if (reassignShift.updated.id) {
      this.shiftTimelineDraftHelperService.updateShiftsToUpdateMap(
        reassignShift.updated.id,
        reassignShift.currentUserId,
      );

      this.shiftTimelineDraftHelperService.updateTrackedAssignmentsMap(
        reassignShift.original.id,
        reassignShift.currentUserId,
        reassignShift.previousUserId,
      );

      await this.fetchAssignmentOptimizerKpis();
    }
  }

  async unassignShiftAndUpdateUsersKpis(
    unassignShift: UnassignShift,
  ): Promise<void> {
    if (unassignShift.updated.id) {
      this.shiftTimelineDraftHelperService.updateShiftsToUpdateMap(
        unassignShift.updated.id,
        null,
      );

      this.shiftTimelineDraftHelperService.updateTrackedAssignmentsMap(
        unassignShift.original.id,
        undefined,
        unassignShift.previousUserId,
      );

      await this.fetchAssignmentOptimizerKpis();
    }
  }

  private async fetchAssignmentOptimizerKpis(): Promise<void> {
    const assignmentData = await firstValueFrom(
      this.assignmentsGanttChartService.assignmentData$,
    );
    this.shiftTimelineDraftHelperService.fetchAssignmentOptimizerKpis(
      assignmentData,
    );
  }

  toggleKPICard(): void {
    this.isKPICardOpen = !this.isKPICardOpen;
  }

  onGanttChartReady(
    assignmentsGanttChartService: AssignmentsGanttChartService,
  ): void {
    this.assignmentsGanttChartService = assignmentsGanttChartService;
  }
}
